import { X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { InviteForm } from './invite-form';
import { MemberList } from './member-list';
import { PendingInvitationsList } from './pending-invitations-list';
import { useCaseMembers, useDeleteInvitation } from './use-case-members';

type MembersPanelProps = {
  readonly caseId: string;
  readonly canInvite: boolean;
  readonly onClose: () => void;
};

export function MembersPanel({ caseId, canInvite, onClose }: MembersPanelProps) {
  const { data, isLoading, isError } = useCaseMembers(caseId);
  const deleteInvitation = useDeleteInvitation(caseId);

  return (
    <aside
      className="fixed inset-y-0 right-0 z-30 flex w-full max-w-sm flex-col border-l p-4 shadow-2xl"
      style={{ background: 'var(--color-surface)', borderColor: 'rgba(255,255,255,0.07)' }}
    >
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-text-primary">Medlemmer</h2>
          <p className="mt-0.5 text-xs text-text-tertiary">Tilgang og ventende invitasjoner.</p>
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1 text-text-tertiary hover:bg-white/6 hover:text-text-secondary"
          aria-label="Lukk medlemspanel"
        >
          <X size={15} />
        </button>
      </div>

      {canInvite && <InviteForm caseId={caseId} />}

      {isError && <p className="mt-4 text-xs text-danger/80">Kunne ikke laste medlemmer.</p>}
      {isLoading && <p className="mt-4 text-xs text-text-tertiary">Laster medlemmer...</p>}
      {deleteInvitation.isError && (
        <p className="mt-4 text-xs text-danger/80">Kunne ikke slette invitasjonen.</p>
      )}

      {data && (
        <div className="mt-5 flex flex-col gap-5 overflow-y-auto">
          <MemberList members={data.members} />
          <PendingInvitationsList
            invitations={data.pendingInvitations}
            canDelete={canInvite}
            isDeleting={deleteInvitation.isPending}
            onDelete={(invitationId) => deleteInvitation.mutate(invitationId)}
          />
        </div>
      )}

      <Button className="mt-auto" variant="ghost" onClick={onClose}>
        Lukk
      </Button>
    </aside>
  );
}
